import { getRecordValue } from 'src/modules/shared/utils/record.util';
import React, { useMemo } from 'react';
import {
  ReactFlow,
  Background,
  BackgroundVariant,
  Controls,
  MiniMap,
  MarkerType,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  type Node as RFNode,
  type Edge as RFEdge,
  type NodeProps as RFNodeProps,
  type Connection,
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  type EdgeProps as RFEdgeProps,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import Tooltip from 'src/modules/shared/components/atoms/tooltips/tooltip';
import { EmptyState } from 'src/modules/shared/components/atoms/states/empty-state';
import { normalizeDefinition } from 'src/modules/tools/utils/workflow-definition.util';
import type {
  NormalizedDefinition,
  StepsMap,
  WorkflowDefinitionDraft,
} from 'src/modules/tools/utils/workflow-definition.type';

const NODE_WIDTH = 240;
const NODE_HEIGHT = 80;
const GAP_X = 40;
const GAP_Y = 80;
export const WORKFLOW_START_NODE_ID = '__start__';
const START_NODE_ID = WORKFLOW_START_NODE_ID;
const START_NODE_SIZE = 36;
const END_NODE_ID = '__end__';
const END_NODE_SIZE = 36;

type CanvasEdgeKind = 'start' | 'next' | 'auto' | 'final';

/** Canvas Edge Data contract. */
interface CanvasEdgeData {
  /** Kind of connection represented by this edge. */
  kind: CanvasEdgeKind;
  /** Removes this connection. */
  onRemove?: (edge: RFEdge) => void;
}

/** Step Node Data contract. */
interface StepNodeData extends Record<string, unknown> {
  /** Text displayed on the workflow node. */
  label: React.ReactNode;
  /** Identifier of the action executed by the step. */
  actionId?: string;
  /** Whether workflow editing controls are available. */
  editable?: boolean;
  /** Whether this node represents the final step. */
  isFinal?: boolean;
  /** Requests a new step connected to the selected source. */
  onAddStep?: (sourceId: string) => void;
  /** Removes the selected step from the workflow. */
  onDeleteStep?: (stepId: string) => void;
  /** Whether the step cannot be reached from the initial step. */
  isUnreachable?: boolean;
}

/** End Node Data contract. */
interface EndNodeData extends Record<string, unknown> {
  /** Whether to show the control for adding a final step. */
  showAddFinal?: boolean;
  /** Requests creation of the final workflow step. */
  onAddFinalStep?: () => void;
}

/** Start Node Data contract. */
interface StartNodeData extends Record<string, unknown> {
  /** Whether to show the control for adding an initial step. */
  showAddInitial?: boolean;
  /** Requests creation of the initial workflow step. */
  onAddInitialStep?: () => void;
}

const computeLevels = (steps: StepsMap, initialStepId: string) => {
  const levelMap = new Map<string, number>();
  const visited = new Set<string>();
  const queue: { id: string; level: number }[] = [];

  if (initialStepId && getRecordValue(steps, initialStepId)) {
    queue.push({ id: initialStepId, level: 0 });
    visited.add(initialStepId);
    levelMap.set(initialStepId, 0);
  }

  while (queue.length) {
    const { id, level } = queue.shift()!;
    const step = getRecordValue(steps, id);
    const nexts = step?.nextStepIds ?? [];
    for (const nextId of nexts) {
      if (getRecordValue(steps, nextId) && !visited.has(nextId)) {
        visited.add(nextId);
        levelMap.set(nextId, level + 1);
        queue.push({ id: nextId, level: level + 1 });
      }
    }
  }

  for (const id of Object.keys(steps)) {
    if (!levelMap.has(id)) levelMap.set(id, 0);
  }

  const levels: Record<string, string[]> = {};
  let maxLevel = 0;
  for (const [id, lvl] of levelMap.entries()) {
    const ids = getRecordValue(levels, String(lvl)) ?? [];
    ids.push(id);
    levels[lvl] = ids;
    if (lvl > maxLevel) maxLevel = lvl;
  }

  return { levels, maxLevel };
};

const buildNodes = (
  steps: StepsMap,
  initialStepId: string,
  definition: NormalizedDefinition,
  editable: boolean,
  onAddStep?: (sourceId: string) => void,
  onDeleteStep?: (stepId: string) => void,
  canAddFinalStep?: boolean,
  onAddFinalStep?: () => void,
  reachable?: Set<string>
): RFNode[] => {
  const { levels } = computeLevels(steps, initialStepId);

  const nodes: RFNode[] = [];
  Object.entries(levels).forEach(([levelStr, ids]) => {
    const level = Number(levelStr);
    ids.forEach((id, index) => {
      const step = steps[id];

      nodes.push({
        id,
        position: {
          x: index * (NODE_WIDTH + GAP_X),
          y: level * (NODE_HEIGHT + GAP_Y),
        },
        data: {
          label: (
            <div className="text-xs">
              <div
                className="font-semibold truncate max-w-[220px]"
                title={step.id}
              >
                {step.id}
              </div>
              <Tooltip content={step.actionId} placement="top" size="xs">
                <div className="text-gray-600 truncate max-w-[220px]">
                  {step.actionId}
                </div>
              </Tooltip>
            </div>
          ),
          editable,
          onAddStep,
          onDeleteStep,
          isUnreachable: reachable ? !reachable.has(id) : false,
        } as StepNodeData,
        style: { width: NODE_WIDTH, height: NODE_HEIGHT, cursor: 'pointer' },
        type: 'step',
      });
    });
  });

  const initialNode = nodes.find((n) => n.id === initialStepId);
  const initialX = initialNode?.position.x ?? 0;
  const startX = initialX + (NODE_WIDTH - START_NODE_SIZE) / 2;
  const startY = -START_NODE_SIZE - GAP_Y / 2;
  nodes.push({
    id: START_NODE_ID,
    position: { x: startX, y: startY },
    data: {
      showAddInitial: editable && !definition.initialStepId,
      onAddInitialStep: onAddStep ? () => onAddStep(START_NODE_ID) : undefined,
    } as StartNodeData,
    style: {
      width: START_NODE_SIZE,
      height: START_NODE_SIZE,
      borderRadius: 9999,
      background: '#F8FAFC',
      border: '1px solid #CBD5E1',
      boxShadow: '0 1px 1px rgba(0,0,0,0.03)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    type: 'start',
    draggable: false,
    selectable: false,
  });

  const terminalIds = Object.values(steps)
    .filter((s) => s.nextStepIds.length === 0)
    .map((s) => s.id);
  const stepNodesForLayout = nodes.filter((n) => n.id !== START_NODE_ID);
  const bottomY =
    stepNodesForLayout.length > 0
      ? stepNodesForLayout.reduce((acc, n) => Math.max(acc, n.position.y), 0)
      : 0;
  const terminalNodes = nodes.filter((n) => terminalIds.includes(n.id));
  const endXBase = terminalNodes.length
    ? terminalNodes.reduce((acc, n) => acc + n.position.x, 0) /
      terminalNodes.length
    : nodes[0]?.position.x ?? 0;
  const endX = endXBase + (NODE_WIDTH - END_NODE_SIZE) / 2;
  const endY = bottomY + NODE_HEIGHT + GAP_Y / 2;
  nodes.push({
    id: END_NODE_ID,
    position: { x: endX, y: endY },
    style: {
      width: END_NODE_SIZE,
      height: END_NODE_SIZE,
      borderRadius: 9999,
      background: '#F8FAFC',
      border: '1px solid #CBD5E1',
      boxShadow: '0 1px 1px rgba(0,0,0,0.03)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    type: 'end',
    draggable: false,
    selectable: false,
    data: {
      showAddFinal: editable && !!canAddFinalStep,
      onAddFinalStep,
    } as EndNodeData,
  });

  if (definition.finalStep) {
    const finalId = definition.finalStep.id;
    const finalX = endXBase;
    const finalY = endY + NODE_HEIGHT + GAP_Y / 2;
    nodes.push({
      id: finalId,
      position: { x: finalX, y: finalY },
      data: {
        label: (
          <div className="text-xs">
            <div
              className="font-semibold truncate max-w-[220px]"
              title={finalId}
            >
              {finalId}
            </div>
            <Tooltip
              content={definition.finalStep.actionId}
              placement="top"
              size="xs"
            >
              <div className="text-gray-600 truncate max-w-[220px]">
                {definition.finalStep.actionId}
              </div>
            </Tooltip>
          </div>
        ),
        editable,
        onAddStep,
        onDeleteStep,
        isFinal: true,
        isUnreachable: reachable ? !reachable.has(finalId) : false,
      } as StepNodeData,
      style: { width: NODE_WIDTH, height: NODE_HEIGHT, cursor: 'pointer' },
      type: 'step',
    });
  }

  return nodes;
};

const buildEdges = (
  steps: StepsMap,
  definition: NormalizedDefinition
): RFEdge[] => {
  const edges: RFEdge[] = [];

  if (definition.initialStepId) {
    edges.push({
      id: `${START_NODE_ID}->${definition.initialStepId}`,
      source: START_NODE_ID,
      target: definition.initialStepId,
      markerEnd: { type: MarkerType.ArrowClosed },
      data: { kind: 'start' } satisfies CanvasEdgeData,
    });
  }

  for (const step of Object.values(steps)) {
    for (const next of step.nextStepIds) {
      edges.push({
        id: `${step.id}->${next}`,
        source: step.id,
        target: next,
        markerEnd: { type: MarkerType.ArrowClosed },
        data: { kind: 'next' } satisfies CanvasEdgeData,
      });
    }
  }

  const terminalIds = Object.values(steps)
    .filter((s) => s.nextStepIds.length === 0)
    .map((s) => s.id);

  for (const terminal of terminalIds) {
    edges.push({
      id: `${terminal}->${END_NODE_ID}`,
      source: terminal,
      target: END_NODE_ID,
      markerEnd: { type: MarkerType.ArrowClosed },
      data: { kind: 'auto' } satisfies CanvasEdgeData,
    });
  }

  if (definition.finalStep) {
    const finalId = definition.finalStep.id;
    edges.push({
      id: `${END_NODE_ID}->${finalId}`,
      source: END_NODE_ID,
      target: finalId,
      markerEnd: { type: MarkerType.ArrowClosed },
      data: { kind: 'final' } satisfies CanvasEdgeData,
    });
  }

  return edges;
};

/** Workflow Canvas Props contract. */
interface WorkflowCanvasProps {
  /** Workflow definition displayed by the editor. */
  definition?: WorkflowDefinitionDraft;
  /** Identifier of the currently selected workflow step. */
  selectedStepId?: string;
  /** Selects a workflow step in the editor. */
  onSelectStep?: (stepId: string) => void;
  /** Whether workflow editing controls are available. */
  editable?: boolean;
  /** Connects the specified workflow nodes. */
  onAddConnection?: (sourceId: string, targetId: string) => void;
  /** Removes the connection between the specified nodes. */
  onRemoveConnection?: (sourceId: string, targetId: string) => void;
  /** Requests a new step connected to the selected source. */
  onAddStep?: (sourceId: string) => void;
  /** Removes the selected step from the workflow. */
  onDeleteStep?: (stepId: string) => void;
  /** Requests creation of the final workflow step. */
  onAddFinalStep?: () => void;
  /** Whether another final step may be added. */
  canAddFinalStep?: boolean;
  /** Identifiers of steps reachable from the initial step. */
  reachableStepIds?: Set<string>;
}

export const WorkflowCanvas: React.FC<WorkflowCanvasProps> = ({
  definition,
  selectedStepId,
  onSelectStep,
  editable = false,
  onAddConnection,
  onRemoveConnection,
  onAddStep,
  onDeleteStep,
  onAddFinalStep,
  canAddFinalStep = false,
  reachableStepIds,
}) => {
  const normalized = useMemo(
    () => normalizeDefinition(definition),
    [definition]
  );
  const { steps, initialStepId } = normalized;

  const initialNodes = useMemo(() => {
    if (!definition) return [];
    return buildNodes(
      steps,
      initialStepId ?? '',
      normalized,
      editable,
      onAddStep,
      onDeleteStep,
      canAddFinalStep,
      onAddFinalStep,
      reachableStepIds
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    steps,
    initialStepId,
    normalized,
    definition,
    editable,
    onAddStep,
    canAddFinalStep,
    onAddFinalStep,
    reachableStepIds,
  ]);

  const initialEdges = useMemo(() => {
    if (!definition) return [];
    return buildEdges(steps, normalized);
  }, [definition, normalized, steps]);

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const handleEdgeButtonRemove = React.useCallback(
    (edge: RFEdge) => {
      setEdges((eds) => eds.filter((e) => e.id !== edge.id));
      if (edge.source && edge.target && edge.target !== END_NODE_ID) {
        onRemoveConnection?.(edge.source, edge.target);
      }
    },
    [onRemoveConnection, setEdges]
  );

  React.useEffect(() => {
    if (!definition) return;
    setEdges(
      initialEdges.map((edge) => {
        const kind = (edge.data as CanvasEdgeData | undefined)?.kind ?? 'auto';
        const canDelete =
          editable && (kind === 'start' || kind === 'next' || kind === 'final');

        if (!canDelete) {
          return {
            ...edge,
            deletable: false,
            data: { kind } satisfies CanvasEdgeData,
          };
        }

        return {
          ...edge,
          type: 'deletable',
          deletable: true,
          data: {
            kind,
            onRemove: handleEdgeButtonRemove,
          } satisfies CanvasEdgeData,
        };
      })
    );
  }, [definition, editable, handleEdgeButtonRemove, initialEdges, setEdges]);

  React.useEffect(() => {
    setNodes(initialNodes);
  }, [initialNodes, setNodes]);

  React.useEffect(() => {
    if (!selectedStepId) {
      setNodes((nds) =>
        nds.map((node) => ({
          ...node,
          selected: false,
        }))
      );
      return;
    }
    setNodes((nds) =>
      nds.map((node) => ({
        ...node,
        selected: node.id === selectedStepId,
      }))
    );
  }, [selectedStepId, setNodes]);

  const handleNodeClick = React.useCallback(
    (_: React.MouseEvent, node: RFNode) => {
      if (node.id === END_NODE_ID) return;
      if (node.id === START_NODE_ID) {
        onSelectStep?.(START_NODE_ID);
        return;
      }
      onSelectStep?.(node.id);
    },
    [onSelectStep]
  );

  const handleConnect = React.useCallback(
    (connection: Connection) => {
      const { source, target } = connection;
      if (!source || !target) return;

      const edgeId = `${source}->${target}`;
      const kind: CanvasEdgeKind =
        source === START_NODE_ID
          ? 'start'
          : source === END_NODE_ID
            ? 'final'
            : 'next';

      setEdges((eds) => {
        if (eds.some((e) => e.id === edgeId)) return eds;
        return [
          ...eds,
          {
            ...connection,
            id: edgeId,
            markerEnd: { type: MarkerType.ArrowClosed },
            type: editable ? 'deletable' : undefined,
            deletable: editable,
            data: editable
              ? ({
                  kind,
                  onRemove: handleEdgeButtonRemove,
                } satisfies CanvasEdgeData)
              : ({ kind } satisfies CanvasEdgeData),
          } as RFEdge,
        ];
      });
      onAddConnection?.(source, target);
    },
    [editable, handleEdgeButtonRemove, onAddConnection, setEdges]
  );

  const handleEdgesDelete = React.useCallback(
    (edgesToDelete: RFEdge[]) => {
      edgesToDelete.forEach((edge) => {
        const { source, target } = edge;
        if (!source || !target) return;
        if (target === END_NODE_ID) return;
        onRemoveConnection?.(source, target);
      });
    },
    [onRemoveConnection]
  );

  if (!definition) {
    return (
      <div className="bg-white border rounded h-[70vh] min-h-[420px] flex items-center justify-center">
        <EmptyState
          // TODO: check when implementing i18n
          title="No hay definición de flujo"
          description="Aún no se ha configurado este flujo."
          appearance="soft"
          variant="neutral"
          size="sm"
        />
      </div>
    );
  }

  return (
    <div className="bg-white border rounded h-[70vh] min-h-[420px]">
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onNodeClick={handleNodeClick}
        onConnect={editable ? handleConnect : undefined}
        onEdgesDelete={editable ? handleEdgesDelete : undefined}
        fitView
        proOptions={{ hideAttribution: true }}
        defaultEdgeOptions={{
          animated: false,
          style: { stroke: '#64748b', strokeWidth: 1.5 },
          markerEnd: { type: MarkerType.ArrowClosed, color: '#64748b' },
        }}
        defaultMarkerColor="#64748b"
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        defaultViewport={{ x: 0, y: 0, zoom: 1 }}
        elementsSelectable
        nodesDraggable={false}
        nodesConnectable={editable}
        edgesFocusable={editable}
        deleteKeyCode={editable ? 'Backspace' : null}
      >
        <MiniMap zoomable pannable />
        <Controls position="bottom-right" />
        <Background variant={BackgroundVariant.Dots} gap={12} size={1} />
      </ReactFlow>
    </div>
  );
};

const StartNode: React.FC<RFNodeProps> = ({ selected, data }) => {
  const typed = data as StartNodeData | undefined;
  const baseClasses =
    'w-full h-full rounded-full border shadow-sm relative transition-colors';
  const stateClasses = selected
    ? 'border-primary-600 bg-white cursor-default'
    : 'border-slate-400 bg-white cursor-pointer hover:border-primary-500 hover:bg-primary-50';

  return (
    <div className={`${baseClasses} ${stateClasses}`}>
      <Handle type="source" position={Position.Bottom} id="start" />
      {typed?.showAddInitial && (
        <button
          type="button"
          className="absolute -right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-slate-300 shadow flex items-center justify-center text-slate-600 hover:bg-primary-50 hover:border-primary-500"
          onClick={(e) => {
            e.stopPropagation();
            typed.onAddInitialStep?.();
          }}
          title="Agregar paso inicial"
        >
          +
        </button>
      )}
    </div>
  );
};

const EndNode: React.FC<RFNodeProps> = ({ data }) => {
  const typed = data as EndNodeData | undefined;
  return (
    <div className="w-full h-full rounded-full border border-slate-300 bg-slate-50 shadow-sm relative cursor-default">
      <Handle type="target" position={Position.Top} id="end" />
      <Handle type="source" position={Position.Bottom} id="end-source" />
      {typed?.showAddFinal && (
        <button
          type="button"
          className="absolute -right-4 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-slate-300 shadow flex items-center justify-center text-slate-600 hover:bg-primary-50 hover:border-primary-500"
          onClick={(e) => {
            e.stopPropagation();
            typed.onAddFinalStep?.();
          }}
          title="Agregar paso final"
        >
          +
        </button>
      )}
    </div>
  );
};

const StepNode: React.FC<RFNodeProps> = ({ id, data, selected }) => {
  const typed = data as StepNodeData | undefined;
  const editable = typed?.editable;
  const isUnreachable = typed?.isUnreachable;
  return (
    <div
      className={`w-full h-full border rounded shadow-sm relative ${
        isUnreachable
          ? 'border-amber-300 bg-amber-50'
          : 'border-slate-300 bg-white'
      }`}
    >
      <Handle type="target" position={Position.Top} id={`${id}-in`} />
      <Handle type="source" position={Position.Bottom} id={`${id}-out`} />
      <div className="p-2 flex items-start justify-between gap-2">
        <div>{typed?.label}</div>
        {isUnreachable && (
          <span className="text-[11px] text-amber-700 bg-amber-100 border border-amber-200 rounded px-1.5 py-0.5">
            No conectado
          </span>
        )}
      </div>
      {editable && !typed.isFinal && (
        <button
          type="button"
          className="absolute -right-3 top-1/2 -translate-y-1/2 w-6 h-6 rounded-full bg-white border border-slate-300 shadow flex items-center justify-center text-slate-600 hover:bg-primary-50 hover:border-primary-500"
          onClick={(e) => {
            e.stopPropagation();
            typed.onAddStep?.(id);
          }}
          title="Agregar paso siguiente"
        >
          +
        </button>
      )}
      {editable && selected && typed.onDeleteStep && (
        <button
          type="button"
          className="absolute -right-2 -top-2 w-4 h-4 rounded-full bg-red-600 text-white text-[10px] leading-none flex items-center justify-center shadow-sm hover:bg-red-700"
          onClick={(e) => {
            e.stopPropagation();
            typed.onDeleteStep?.(id);
          }}
          title="Eliminar paso"
          aria-label="Eliminar paso"
        >
          ×
        </button>
      )}
    </div>
  );
};

const nodeTypes = {
  start: StartNode,
  end: EndNode,
  step: StepNode,
};

const DeletableEdge: React.FC<RFEdgeProps> = (props) => {
  const {
    id,
    sourceX,
    sourceY,
    targetX,
    targetY,
    sourcePosition,
    targetPosition,
    markerEnd,
    style,
    data,
    source,
    target,
    selected,
    deletable,
  } = props;

  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const handleRemoveClick = React.useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      const typedData = data as CanvasEdgeData | undefined;
      typedData?.onRemove?.({ id, source, target } as RFEdge);
    },
    [data, id, source, target]
  );

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd ?? 'arrowclosed'}
        style={style}
        interactionWidth={30}
      />
      {selected && deletable && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px, ${labelY}px)`,
              pointerEvents: 'all',
              zIndex: 5,
            }}
          >
            <button
              onClick={handleRemoveClick}
              className="w-4 h-4 rounded-full bg-red-600 text-white text-[10px] leading-none flex items-center justify-center shadow-sm hover:bg-red-700"
              title="Eliminar conexión"
              aria-label="Eliminar conexión"
            >
              ×
            </button>
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

const edgeTypes = {
  deletable: DeletableEdge,
};

export default WorkflowCanvas;
